import React, { memo, useState, useEffect } from "react";
import { Handle, useUpdateNodeInternals } from "react-flow-renderer";
import { Box, TextField, Typography } from "@mui/material";
import SearchIcon from '@mui/icons-material/SearchOutlined';
import { NodeWrapper } from "./NodeWrapper";
import { i18n } from "../../../translate/i18n";
import { mkHeaderStyle, HEADER_ICON_STYLE, HEADER_TEXT_STYLE, NODE_BODY_STYLE, NODE_INFO_ROW, mkHandleStyle } from "./nodeStyles";


import { Tooltip, IconButton } from '@material-ui/core';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#1e293b',
    color: '#f8fafc',
    maxWidth: 380,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #334155',
    padding: '12px 14px',
    borderRadius: '8px',
    fontFamily: "'Inter', 'Roboto', sans-serif",
    lineHeight: 1.5,
  },
  arrow: { color: '#1e293b' },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
  pulseIcon: {
    color: '#ffffff',
    borderRadius: '50%',
    animation: '$glowPulse 1.5s infinite',
  },
  '@keyframes glowPulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.9)' },
    '70%': { boxShadow: '0 0 0 8px rgba(255, 255, 255, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)' },
  },
}));

export default memo(({ data, isConnectable, id, selected }) => {
  const classes = useStyles();
  const [inputVariable, setInputVariable] = useState(data.inputVariable || '');
  const [listVariable, setListVariable] = useState(data.outputVariable || data.listVariable || '');
  const [optionsVariable, setOptionsVariable] = useState('v_opciones');
  useEffect(() => { data.outputVariableAmbiguous = 'v_opciones'; data.optionsVariable = 'v_opciones'; }, [data]);
  const [optionsStr, setOptionsStr] = useState(data.optionsList || '');

  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    updateNodeInternals(id);
  }, [id, updateNodeInternals, data]);

  useEffect(() => {
    if (data.optionsList) {
      setOptionsStr(data.optionsList);
    } else if (data.options) {
      const opts = Array.isArray(data.options) ? data.options : [];
      const mapped = opts.map(o => `${o.id} - ${o.name}`).join('\n');
      setOptionsStr(mapped);
      data.optionsList = mapped; // Migrate to new format
    }
  }, [data.options, data.optionsList]);

  const handleOptionsChange = (e) => { const val = e.target.value; setOptionsStr(val); data.optionsList = val; };

  return (
    <NodeWrapper id={id} selected={selected} color="#D500F9">
      <Handle 
        type="target" 
        position="left" 
        id="a" 
        style={mkHandleStyle("#D500F9", { left: "-12px", top: "20px", transform: "translateY(-50%)" })} 
      />

      <div style={{ ...mkHeaderStyle("#D500F9"), justifyContent: "space-between" }}>
  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <SearchIcon style={HEADER_ICON_STYLE} />
        <div style={HEADER_TEXT_STYLE}>
          {data.name || (i18n.t("flowBuilder.nodes.listMatch.title") || "Búsqueda en Lista")}
        </div>
      </div>
  <div style={{ display: "flex", alignItems: "center", marginLeft: "auto" }}>
    <HtmlTooltip
            title={
              <React.Fragment>
                <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#38bdf8' }}>
                  💡 {i18n.t("flowBuilder.tooltips.listMatch.title")}
                </div>
                <div style={{ marginBottom: '0' }}>
                  <div dangerouslySetInnerHTML={{ __html: i18n.t("flowBuilder.tooltips.listMatch.body") }} />
                </div>
              </React.Fragment>
            }
            placement="right"
          >
            <IconButton size="small" style={{ padding: '4px', marginLeft: '2px' }} className={classes.pulseIcon}>
              <HelpOutlineIcon style={{ fontSize: '15px' }} />
            </IconButton>
          </HtmlTooltip>
  </div>
</div>

      <div style={{ ...NODE_BODY_STYLE, display: "flex", flexDirection: "column", gap: "12px" }}>
        <TextField 
          label={i18n.t("flowBuilder.nodes.listMatch.inputVar") || "Variable a Evaluar"} 
          placeholder={i18n.t("flowBuilder.nodes.listMatch.inputVarPlaceholder") || "Ej: {{v_sector}}"}
          size="small" 
          fullWidth 
          value={inputVariable} 
          onChange={e => { setInputVariable(e.target.value); data.inputVariable = e.target.value; }} 
          sx={{ 
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              '& textarea::-webkit-scrollbar': { width: '6px' },
              '& textarea::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: '4px' }
            }} 
        />

        <Box sx={{ position: 'relative' }}>
          <div style={{ position: 'absolute', right: '12px', top: '-8px', zIndex: 5, fontSize: '9px', background: '#f0fdf4', padding: '2px 6px', borderRadius: '4px', border: '1px solid #bbf7d0', color: '#16a34a', fontWeight: 'bold' }}>{i18n.t("flowBuilder.nodes.listMatch.exact") || "Exacto"}</div>
          <TextField 
            label={i18n.t("flowBuilder.nodes.listMatch.exactVar") || "Variable Exacta"}
            placeholder={i18n.t("flowBuilder.nodes.listMatch.exactVarPlaceholder") || "Ej: v_sector_id"} 
            size="small" 
            fullWidth 
            value={listVariable} 
            onChange={e => { setListVariable(e.target.value); data.outputVariable = e.target.value; data.listVariable = e.target.value; }} 
            sx={{ 
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              '& textarea::-webkit-scrollbar': { width: '6px' },
              '& textarea::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: '4px' }
            }} 
            
          />
          <Handle 
            type="source" 
            position="right" 
            id="exact" 
            style={mkHandleStyle("#4caf50", { right: -24, top: "50%", transform: "translateY(-50%)" })} 
            isConnectable={isConnectable} 
          />
        </Box>

        <Box sx={{ position: 'relative' }}>
          <div style={{ position: 'absolute', right: '12px', top: '-8px', zIndex: 5, fontSize: '9px', background: '#fff7ed', padding: '2px 6px', borderRadius: '4px', border: '1px solid #fed7aa', color: '#ea580c', fontWeight: 'bold' }}>{i18n.t("flowBuilder.nodes.listMatch.ambiguous") || "Ambiguo"}</div>
          <TextField 
            label={i18n.t("flowBuilder.nodes.listMatch.ambiguousVar") || "Variable Ambigua"}
            placeholder={i18n.t("flowBuilder.nodes.listMatch.ambiguousVarPlaceholder") || "Ej: v_opciones"} 
            size="small" 
            fullWidth 
            value={optionsVariable}
            disabled
            sx={{ 
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              '& textarea::-webkit-scrollbar': { width: '6px' },
              '& textarea::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: '4px' }
            }} 
            
          />
          <Handle 
            type="source" 
            position="right" 
            id="ambiguous" 
            style={mkHandleStyle("#ff9800", { right: -24, top: "50%", transform: "translateY(-50%)" })} 
            isConnectable={isConnectable} 
          />
        </Box>

        <Box sx={{ position: 'relative' }}>
          <div style={{ position: 'absolute', right: '12px', top: '-8px', zIndex: 5, fontSize: '9px', background: '#fef2f2', padding: '2px 6px', borderRadius: '4px', border: '1px solid #fecaca', color: '#dc2626', fontWeight: 'bold' }}>{i18n.t("flowBuilder.nodes.listMatch.notFound") || "No Encontrado"}</div>
          <TextField 
            label={i18n.t("flowBuilder.nodes.listMatch.optionsList") || "Lista de Opciones"} 
            size="small" 
            multiline 
            rows={4} 
            fullWidth 
            value={optionsStr} 
            onChange={handleOptionsChange} 
            placeholder={i18n.t("flowBuilder.nodes.listMatch.optionsListPlaceholder") || "1 - Ventas\n2 - Soporte"}
            sx={{ 
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
              '& textarea::-webkit-scrollbar': { width: '6px' },
              '& textarea::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: '4px' }
            }} 
            
          />
          <Handle 
            type="source" 
            position="right" 
            id="not_found" 
            style={mkHandleStyle("#f44336", { right: -24, top: "50%", transform: "translateY(-50%)" })} 
            isConnectable={isConnectable} 
          />
        </Box>
        
        
      </div>
    </NodeWrapper>
  );
});
